import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['draft', 'published', 'archived'];
const VIS = ['public', 'authenticated', 'platform'];

function statusColor(s) {
  if (s === 'published') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function PlatformKnowledgeArticlesListPage() {
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [catInput, setCatInput] = useState('');
  const [appliedCat, setAppliedCat] = useState('');
  const [visInput, setVisInput] = useState('');
  const [appliedVis, setAppliedVis] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedCat) params.set('category', appliedCat);
    if (appliedVis) params.set('visibility', appliedVis);
    try {
      const res = await platformApiFetch(`/api/platform/knowledge-base/articles?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedCat, appliedVis]);

  useEffect(() => {
    if (ready) load();
  }, [load, ready]);

  const apply = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedCat(catInput.trim());
    setAppliedVis(visInput);
  };

  const rows = data?.articles || [];
  const total = data?.pagination?.total ?? 0;

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Global knowledge base</Typography>
        {isSuper && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/platform/knowledge-base/articles/new')}>
            New article
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Platform-wide help articles (not tied to a tenant). Slugs must be unique among global articles. Staff can browse;
        only super admins can create or edit.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <TextField
            size="small"
            label="Status"
            select
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </TextField>
          <TextField size="small" label="Category" value={catInput} onChange={(e) => setCatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && apply()} />
          <TextField size="small" label="Visibility" select value={visInput} onChange={(e) => setVisInput(e.target.value)} sx={{ minWidth: 160 }} SelectProps={{ native: true }}>
            <option value="">All</option>
            {VIS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </TextField>
          <Button variant="outlined" onClick={apply}>
            Apply
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.title}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.slug}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.visibility}</TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/platform/knowledge-base/articles/${row.id}`} size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      {isSuper && (
                        <IconButton component={Link} to={`/platform/knowledge-base/articles/${row.id}/edit`} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No global articles.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
